import { ReactElement, ReactNode, createContext, use, useMemo } from 'react';

import { Styles, css, sva } from '@flipster/styled-system/css';
import { center, flex } from '@flipster/styled-system/patterns';

import { useControllableState } from '../misc/state/use-controllable-state';
import { StatusBadge } from './status-badge';

interface SegmentedControlContextType<T extends string> {
  slots?: ReturnType<typeof segmentedControlRecipe.raw>;
  value?: T;
  setValue?: (value: T) => void;
}
const SegmentedControlContext = createContext<SegmentedControlContextType<string>>({});

interface SegmentedControlRootProps<T> {
  size?: 'small' | 'medium';
  defaultValue?: T;
  value?: T;
  onChange?: (value: T) => void;
  children: ReactNode;
  css?: Styles;
}

const SegmentedControlRoot = <T extends string>({
  size = 'medium',
  defaultValue,
  value,
  onChange,
  children,
  css: cssProps,
}: SegmentedControlRootProps<T>) => {
  const recipe = useMemo(() => segmentedControlRecipe.raw({ size }), [size]);

  const [controlValue, setControlValue] = useControllableState<T>({
    defaultValue,
    value,
    onChange,
  });

  return (
    <SegmentedControlContext
      value={{ slots: recipe, value: controlValue, setValue: setControlValue } as SegmentedControlContextType<string>}
    >
      <div role='tablist' className={css(recipe.control, cssProps)}>
        {children}
      </div>
    </SegmentedControlContext>
  );
};

interface SegmentedControlItemProps<T> {
  value: T;
  redDot?: boolean;
  children: ReactNode;
}

const SegmentedControlItem = <T extends string>({ value, redDot, children }: SegmentedControlItemProps<T>) => {
  const {
    slots,
    value: currentValue,
    setValue,
  } = use(SegmentedControlContext) as unknown as SegmentedControlContextType<T>;
  return (
    <button
      type='button'
      role='tab'
      tabIndex={0}
      aria-selected={currentValue === value}
      onClick={() => setValue?.(value)}
      className={css(slots?.item)}
      title={typeof children === 'string' ? children : undefined}
    >
      <StatusBadge positionType='text' color='red' visible={Boolean(redDot)} css={slots?.statusBadge}>
        {children}
      </StatusBadge>
    </button>
  );
};

type SegmentedControl<T extends string> = {
  (props: SegmentedControlRootProps<T>): ReactElement | null;
  Item: (props: SegmentedControlItemProps<T>) => ReactElement | null;
};

const SegmentedControl = Object.assign(SegmentedControlRoot, {
  Item: SegmentedControlItem,
});

SegmentedControlRoot.displayName = 'SegmentedControl';
SegmentedControlItem.displayName = 'SegmentedControl.Item';

export function createSegmentedControl<T extends string>(): SegmentedControl<T> {
  return SegmentedControl as unknown as SegmentedControl<T>;
}

const segmentedControlRecipe = sva({
  slots: ['control', 'item', 'statusBadge'],
  base: {
    control: {
      // border 1px 뺀 3px
      ...flex.raw({ p: 3, border: '1px solid', borderColor: 'border.default.level-1', rounded: 1000 }),
    },
    item: {
      ...center.raw({
        textAlign: 'center',
        minW: 0,
        flex: 1,
        color: 'content.default.level-4',
        rounded: 1000,
        transition: 'background-color 270ms cubic-bezier(0.51, -0.1, 0.56, 1.04)',
        _focusVisible: {
          outline: '2px solid',
          outlineColor: 'border.accent.info',
          outlineOffset: 0,
          rounded: 1000,
        },
        _selected: {
          bgColor: 'background.default.level-3',
        },
        '&:not([aria-selected=true], [data-selected])': {
          _hover: {
            color: 'content.interaction.hover',
          },
        },
      }),
    },
    statusBadge: {
      display: 'inline-block',
      pl: 7,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordBreak: 'break-word',
    },
  },
  variants: {
    size: {
      medium: {
        item: {
          textStyle: 'Text/M/Monospace/Strong',
          h: 36,
          px: 12,
        },
        statusBadge: {
          display: '-webkit-box',
          boxOrient: 'vertical',
          lineClamp: 2,
        },
      },
      small: {
        item: {
          textStyle: 'Text/S/Monospace/Strong',
          h: 22,
          px: 6,
        },
        statusBadge: {
          whiteSpace: 'nowrap',
        },
      },
    },
  },
});
